import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Search, ArrowUpRight } from 'lucide-react'
import { BlogBreadcrumbs } from '@/components/marketing/blog-breadcrumbs'
import { HeroTitle } from '@/components/marketing/hero-title'
import {
  blogPosts,
  blogPath,
  BLOG_ARCHIVE_PATHS,
  blogDate,
  readingMinutes,
  type BlogPost,
} from '@/config/blog/posts'
import { NotFoundPage } from './NotFoundPage'
import '@/styles/blog.css'

function PostCard({ post }: { post: BlogPost }) {
  return (
    <article className="blog-card">
      <div className="blog-meta">
        <span className="blog-category">{post.category}</span>
        <time dateTime={post.published_at}>{blogDate(post.published_at)}</time>
      </div>
      <h2>
        <Link to={blogPath(post)}>{post.title}</Link>
      </h2>
      <p className="blog-excerpt">
        {post.excerpt.length > 180
          ? `${post.excerpt.slice(0, 177).trimEnd()}…`
          : post.excerpt}
      </p>
      <div className="blog-card-footer">
        <span>
          By {post.author.name} <span aria-hidden="true">·</span>{' '}
          {readingMinutes(post.content)} min read
        </span>
        <Link className="blog-read" to={blogPath(post)}>
          Read post <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </div>
    </article>
  )
}

export function BlogsPage() {
  const { pathname } = useLocation()
  const archive = pathname.replace(/\/+$/, '')
  const isArchive = archive.startsWith('/blogs/')
  const posts = isArchive
    ? blogPosts.filter((post) => blogPath(post).startsWith(`${archive}/`))
    : blogPosts
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const [page, setPage] = useState(1)
  const categories = ['All', ...new Set(posts.map((post) => post.category))]
  const filtered = posts.filter(
    (post) =>
      (category === 'All' || post.category === category) &&
      `${post.title} ${post.excerpt.length > 180 ? `${post.excerpt.slice(0, 177).trimEnd()}…` : post.excerpt} ${post.content} ${post.tags.join(' ')}`
        .toLowerCase()
        .includes(query.toLowerCase())
  )
  if (isArchive && !BLOG_ARCHIVE_PATHS.includes(archive as `/blogs/${string}`))
    return <NotFoundPage />
  return (
    <main className="newsroom blog">
      <section className="newsroom-hero marketing-section">
        <div className="marketing-container">
          <div className="hero-stack hero-stack--start">
            {isArchive && <BlogBreadcrumbs path={archive} />}
            {isArchive && (
              <HeroTitle className="newsroom-hero__title">
                {`Blog — ${archive.slice(7)}`}
              </HeroTitle>
            )}
            <p className="newsroom-hero__lead">
              Stories, updates, and perspectives from Podium Markets.
            </p>
          </div>
        </div>
      </section>
      <section className="marketing-section blog-archive">
        <div className="marketing-container">
          <div className="blog-toolbar">
            <h2 className="newsroom-section__title">All posts</h2>
          </div>
          <div className="blog-controls">
            <div className="blog-filters" aria-label="Filter by category">
              {categories.map((value) => (
                <button
                  key={value}
                  aria-pressed={category === value}
                  onClick={() => {
                    setCategory(value)
                    setPage(1)
                  }}
                >
                  {value}
                </button>
              ))}
            </div>
            <label className="blog-search">
              <Search size={16} aria-hidden="true" />
              <span className="sr-only">Search articles</span>
              <input
                type="search"
                placeholder="Search posts"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setPage(1)
                }}
              />
            </label>
          </div>
          <div className="blog-grid">
            {filtered.slice(0, page * 9).map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="blog-empty" role="status">
              {blogPosts.length
                ? 'No posts match your search.'
                : 'Our first blog post is on its way. Check back soon.'}
            </p>
          )}
          {filtered.length > page * 9 && (
            <button className="blog-more" onClick={() => setPage(page + 1)}>
              Load more posts
            </button>
          )}
        </div>
      </section>
    </main>
  )
}
